import { useEffect, useState } from "react";
import { ChevronDown, Globe2, Loader2, PackageSearch, Search, Star, Zap } from "lucide-react";
import api, { inr, fmtErr } from "../../lib/api";
import { toast } from "sonner";

const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-shipany-navy placeholder:text-slate-400 focus:border-shipany-blue focus:outline-none focus:ring-2 focus:ring-shipany-blue/15";

function CountrySelect({ countries, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const selected = countries.find((c) => c.code === value);
  const filtered = countries.filter((c) => !q || c.name.toLowerCase().includes(q.toLowerCase()) || c.code.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="relative" data-testid="rc-intl-country-select">
      <button type="button" onClick={() => setOpen(!open)} data-testid="rc-intl-country-trigger"
        className={`${inputCls} flex items-center justify-between text-left`}>
        {selected ? <span>{selected.name} <span className="mono text-xs text-slate-400">{selected.code}</span></span> : <span className="text-slate-400">Search {countries.length || ""} countries…</span>}
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute z-30 mt-2 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl" data-testid="rc-intl-country-dropdown">
          <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2">
            <Search className="h-4 w-4 text-slate-400" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid="rc-intl-country-search"
              placeholder="Type country name or code…" className="w-full py-1.5 text-sm text-shipany-navy placeholder:text-slate-400 focus:outline-none" />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {filtered.map((c) => (
              <button key={c.code} type="button" data-testid={`rc-intl-country-option-${c.code}`}
                onClick={() => { onChange(c.code); setOpen(false); setQ(""); }}
                className="flex w-full items-center justify-between px-4 py-2.5 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50">
                <span>{c.name}</span><span className="mono text-xs text-slate-400">{c.code}</span>
              </button>
            ))}
            {!filtered.length && <p className="px-4 py-5 text-center text-xs text-slate-400" data-testid="rc-intl-country-empty">No matching country</p>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function RateCalculator() {
  const [tab, setTab] = useState("domestic");
  const [form, setForm] = useState({ pickup_pincode: "", delivery_pincode: "", weight: "1", length: "10", breadth: "10", height: "10", payment_mode: "Prepaid", cod_amount: "" });
  const [intl, setIntl] = useState({ destination_country: "", postal_code: "", state: "", weight: "1", length: "10", breadth: "10", height: "10", declared_value: "2000" });
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [mode, setMode] = useState("balanced");

  useEffect(() => {
    api.get("/intl/countries").then(({ data }) => setCountries(data || [])).catch(() => {});
  }, []);

  const calc = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post(`/rates/optimize?mode=${mode}`, {
        pickup_pincode: form.pickup_pincode,
        delivery_pincode: form.delivery_pincode,
        weight: parseFloat(form.weight) || 0,
        length: parseFloat(form.length) || 10,
        breadth: parseFloat(form.breadth) || 10,
        height: parseFloat(form.height) || 10,
        payment_mode: form.payment_mode,
        cod_amount: parseFloat(form.cod_amount) || 0,
      });
      setResult({ kind: "domestic", data });
      if (!data.quotes.length) toast.info("No couriers available for this lane/weight");
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  const calcIntl = async (e) => {
    e.preventDefault();
    if (!intl.destination_country) {
      toast.error("Select a destination country");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const declared = parseFloat(intl.declared_value) || 1000;
      const { data } = await api.post("/intl/rates", {
        destination_country: intl.destination_country,
        destination_postal_code: intl.postal_code,
        destination_state: intl.state,
        boxes: [{
          weight: parseFloat(intl.weight) || 0.5,
          length: parseFloat(intl.length) || 10,
          width: parseFloat(intl.breadth) || 10,
          height: parseFloat(intl.height) || 10,
          unit: "cm",
          items: [{ name: "General merchandise", quantity: 1, unit_price: declared }],
        }],
      });
      setResult({ kind: "international", data });
      if (!data.quotes.length) toast.info("No services available for this destination/weight");
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  const intlQuotes = result?.kind === "international" ? result.data.quotes || [] : [];
  const zoneVariants = {};
  for (const q of intlQuotes) {
    if (q.zone) (zoneVariants[`${q.courier_name}|${q.service}`] = zoneVariants[`${q.courier_name}|${q.service}`] || new Set()).add(q.zone);
  }

  return (
    <div className="mx-auto max-w-5xl" data-testid="rate-calculator-page">
      <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">Rate Calculator</h1>
      <p className="text-sm text-slate-500">Compare live prices across every enabled courier.</p>

      <div className="mt-5 flex gap-2" data-testid="rc-tabs">
        {[["domestic", "Domestic", PackageSearch], ["international", "International", Globe2]].map(([k, label, Icon]) => (
          <button key={k} type="button" onClick={() => { setTab(k); setResult(null); }} data-testid={`rc-tab-${k}`}
            className={`flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-bold transition-colors ${tab === k ? "bg-shipany-navy text-white" : "bg-white text-slate-500 border border-slate-200 hover:bg-slate-50"}`}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "domestic" && (
        <form onSubmit={calc} className="mt-4 rounded-2xl border border-slate-200 bg-white p-6" data-testid="rate-calculator-form">
          <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-6">
            <div><label className="text-xs font-semibold text-slate-500">Pickup pincode</label>
              <input data-testid="rc-pickup-pincode" className={`${inputCls} mono mt-1`} maxLength={6} value={form.pickup_pincode} onChange={(e) => setForm({ ...form, pickup_pincode: e.target.value })} required /></div>
            <div><label className="text-xs font-semibold text-slate-500">Delivery pincode</label>
              <input data-testid="rc-delivery-pincode" className={`${inputCls} mono mt-1`} value={form.delivery_pincode} onChange={(e) => setForm({ ...form, delivery_pincode: e.target.value })} required /></div>
            <div><label className="text-xs font-semibold text-slate-500">Weight (kg)</label>
              <input data-testid="rc-weight" className={`${inputCls} mono mt-1`} value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} required /></div>
            <div><label className="text-xs font-semibold text-slate-500">L×B×H (cm)</label>
              <div className="mt-1 flex gap-1">
                {["length", "breadth", "height"].map((k) => (
                  <input key={k} data-testid={`rc-dim-${k}`} className={`${inputCls} mono px-2 text-center`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
                ))}
              </div></div>
            <div><label className="text-xs font-semibold text-slate-500">Payment</label>
              <select data-testid="rc-payment-mode" className={`${inputCls} mt-1`} value={form.payment_mode} onChange={(e) => setForm({ ...form, payment_mode: e.target.value })}>
                <option>Prepaid</option><option>COD</option>
              </select></div>
            {form.payment_mode === "COD" && (
              <div><label className="text-xs font-semibold text-slate-500">COD amount (₹)</label>
                <input data-testid="rc-cod-amount" className={`${inputCls} mono mt-1`} value={form.cod_amount} onChange={(e) => setForm({ ...form, cod_amount: e.target.value })} /></div>
            )}
          </div>
          <div className="mt-5">
            <p className="mb-2 text-xs font-semibold text-slate-500">Optimization mode</p>
            <div className="flex flex-wrap gap-2" data-testid="ai-mode-chips">
              {(result?.kind === "domestic" && result.data.modes || [["lowest_cost","Lowest Cost"],["fastest","Fastest"],["lowest_rto","Lowest RTO"],["highest_success","Highest Success"],["balanced","Balanced"],["max_profit","Maximum Profit"]]).map((m) => {
                const [k, l] = Array.isArray(m) ? m : [m.key, m.label];
                return (
                  <button type="button" key={k} onClick={() => setMode(k)} data-testid={`ai-mode-${k}`}
                    className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${mode === k ? "bg-shipany-navy text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>{l}</button>
                );
              })}
            </div>
          </div>
          <button data-testid="rc-calculate-btn" disabled={loading}
            className="mt-5 flex items-center gap-2 rounded-xl bg-shipany-blue px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#0648B3] disabled:opacity-60 glow-blue">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <PackageSearch className="h-4 w-4" />} Calculate Rates
          </button>
        </form>
      )}

      {tab === "international" && (
        <form onSubmit={calcIntl} className="mt-4 rounded-2xl border border-slate-200 bg-white p-6" data-testid="rc-intl-form">
          <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-4">
            <div className="lg:col-span-2"><label className="text-xs font-semibold text-slate-500">Destination country</label>
              <div className="mt-1"><CountrySelect countries={countries} value={intl.destination_country} onChange={(v) => setIntl({ ...intl, destination_country: v })} /></div></div>
            <div><label className="text-xs font-semibold text-slate-500">Destination state / region</label>
              <input data-testid="rc-intl-state" className={`${inputCls} mt-1`} placeholder="e.g. Georgia" value={intl.state} onChange={(e) => setIntl({ ...intl, state: e.target.value })} /></div>
            <div><label className="text-xs font-semibold text-slate-500">Destination postcode</label>
              <input data-testid="rc-intl-postcode" className={`${inputCls} mono mt-1`} value={intl.postal_code} onChange={(e) => setIntl({ ...intl, postal_code: e.target.value })} /></div>
            <div><label className="text-xs font-semibold text-slate-500">Weight (kg)</label>
              <input data-testid="rc-intl-weight" className={`${inputCls} mono mt-1`} value={intl.weight} onChange={(e) => setIntl({ ...intl, weight: e.target.value })} required /></div>
            <div><label className="text-xs font-semibold text-slate-500">L×B×H (cm)</label>
              <div className="mt-1 flex gap-1">
                {["length", "breadth", "height"].map((k) => (
                  <input key={k} data-testid={`rc-intl-dim-${k}`} className={`${inputCls} mono px-2 text-center`} value={intl[k]} onChange={(e) => setIntl({ ...intl, [k]: e.target.value })} />
                ))}
              </div></div>
            <div><label className="text-xs font-semibold text-slate-500">Declared value (₹)</label>
              <input data-testid="rc-intl-declared" className={`${inputCls} mono mt-1`} value={intl.declared_value} onChange={(e) => setIntl({ ...intl, declared_value: e.target.value })} /></div>
          </div>
          <button data-testid="rc-intl-calculate-btn" disabled={loading}
            className="mt-5 flex items-center gap-2 rounded-xl bg-shipany-blue px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#0648B3] disabled:opacity-60 glow-blue">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe2 className="h-4 w-4" />} Calculate International Rates
          </button>
        </form>
      )}

      {result?.kind === "domestic" && (
        <div className="mt-6" data-testid="rc-results">
          {result.data.ai_recommendation && (
            <div className="mb-4 rounded-2xl bg-shipany-navy p-5 text-white" data-testid="ai-recommendation-banner">
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-shipany-cyan">ShipAny · {result.data.ai_recommendation.mode_label}</p>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-200">{result.data.ai_recommendation.explanation}</p>
            </div>
          )}
          <p className="text-xs font-medium text-slate-400">Zone {result.data.zone} · {result.data.zone_name} · Chargeable weight {result.data.chargeable_weight} kg · {result.data.quotes.length} couriers</p>
          <div className="mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {result.data.quotes.map((q) => (
              <div key={q.courier_id} data-testid={`rc-quote-${q.courier_code}`}
                className={`rounded-2xl border bg-white p-5 transition-all hover:-translate-y-0.5 hover:shadow-lg ${q.recommended ? "border-shipany-blue/50 glow-blue" : "border-slate-200"}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-display font-bold text-shipany-navy">{q.courier_name}</p>
                  </div>
                  {q.recommended && <span className="rounded-full bg-shipany-blue px-2 py-0.5 text-[10px] font-bold uppercase text-white">Recommended</span>}
                  {q.ai_score != null && <span className="rounded-full bg-shipany-cyan/15 px-2 py-0.5 text-[10px] font-bold text-shipany-blue" data-testid={`ai-score-${q.courier_code}`}>Score {q.ai_score}</span>}
                </div>
                <p className="mono mt-4 text-2xl font-bold text-shipany-blue">{inr(q.final_price)}</p>
                <div className="mt-3 flex items-center gap-3 text-xs text-slate-500">
                  <span className="flex items-center gap-1"><Zap className="h-3.5 w-3.5 text-shipany-cyan" />{q.eta_days}d ETA</span>
                  <span className="flex items-center gap-1"><Star className="h-3.5 w-3.5 text-amber-400" />{q.rating}</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {q.badges.map((b) => <span key={b} className="rounded-full bg-shipany-cyan/15 px-2 py-0.5 text-[10px] font-bold uppercase text-shipany-blue">{b}</span>)}
                  {q.cod_charge > 0 && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-500">COD {inr(q.cod_charge)}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {result?.kind === "international" && (
        <div className="mt-6" data-testid="rc-intl-results">
          <p className="text-xs font-medium text-slate-400">
            {result.data.country?.name} · billed weight {result.data.totals?.billed} kg
            {result.data.zone_resolved ? ` · Zone ${result.data.zone_resolved} pricing applied` : ""} · duties may apply at destination
          </p>
          <div className="mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {intlQuotes.map((q, i) => (
              <div key={`${q.courier_name}-${q.service}-${q.zone}-${i}`} data-testid={`rc-intl-quote-${i}`}
                className={`rounded-2xl border bg-white p-5 transition-all hover:-translate-y-0.5 hover:shadow-lg ${q.recommended ? "border-shipany-blue/50 glow-blue" : "border-slate-200"}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-display font-bold text-shipany-navy">{q.courier_name}</p>
                    <p className="text-xs capitalize text-slate-400">{q.service}{q.transit_days ? ` · ${q.transit_days}` : ""}</p>
                  </div>
                  {q.recommended && <span className="rounded-full bg-shipany-blue px-2 py-0.5 text-[10px] font-bold uppercase text-white">Cheapest</span>}
                </div>
                <p className="mono mt-4 text-2xl font-bold text-shipany-blue">{inr(q.customer_price)}</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {q.zone && (zoneVariants[`${q.courier_name}|${q.service}`] || new Set()).size > 1 && (
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-500" data-testid={`rc-intl-zone-${i}`}>Zone {q.zone}</span>
                  )}
                  <span className="rounded-full bg-shipany-cyan/15 px-2 py-0.5 text-[10px] font-bold uppercase text-shipany-blue">Tracking included</span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-500">Taxes included</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
